import { observer } from "mobx-react-lite";
import * as React from "react";
import { ColumnFiltersDict } from "src/components/ReactiveTable/components/AntDropdownFilter";
import { RtCtrlFiltration } from "src/components/ReactiveTable/ctrl";
import { RtColumn } from "src/components/ReactiveTable/RtColumn";
import {
  AbstractFilters,
  AbstractRow,
} from "src/components/ReactiveTable/RtTypes";

import * as styles from "./FilterDropdown.module.less";

/**
 * Props для FilterDropdown
 */
export interface FilterDropdownProps<
  TRow extends AbstractRow,
  TFilters extends AbstractFilters,
> {
  column: RtColumn<TRow>;
  store: RtCtrlFiltration<TFilters>;
  columnFilters?: ColumnFiltersDict;
  onClose?: () => void;
}

/**
 * Адаптер для интеграции фильтров из AntDropdownFilter
 * в HeaderCell модульной таблицы
 *
 * Этот компонент является мостом между существующей системой фильтров
 * и новой модульной архитектурой.
 */
export const FilterDropdown = observer(
  <TRow extends AbstractRow, TFilters extends AbstractFilters>(
    props: FilterDropdownProps<TRow, TFilters>,
  ) => {
    const { column, store, columnFilters, onClose } = props;

    // Получаем фильтр для этой колонки
    const filter = columnFilters?.[column.key];

    if (!filter) {
      return null;
    }

    // Получаем текущее значение фильтра
    const filterValue = filter.getValue?.();

    // Создаём props совместимые с Ant Design FilterDropdownProps
    const filterDropdownProps = {
      prefixCls: "",
      setSelectedKeys: (keys: React.Key[]) => {
        // Применяем фильтр через store
        const value = keys.length > 0 ? keys[0] : undefined;
        const filterKey = column.key as keyof TFilters;
        store.changeSingleFilter(
          filterKey,
          value as TFilters[typeof filterKey],
        );
      },
      selectedKeys: filterValue || [],
      confirm: () => {
        // Подтверждение применения фильтра (в модульной таблице применяется сразу)
        onClose?.();
      },
      clearFilters: () => {
        const filterKey = column.key as keyof TFilters;
        store.changeSingleFilter(
          filterKey,
          undefined as TFilters[typeof filterKey],
        );
      },
      filters: [],
      visible: true,
      close: () => {
        // Закрытие dropdown
        onClose?.();
      },
    };

    // Рендерим компонент фильтра
    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div className={styles.container} onClick={(e) => e.stopPropagation()}>
        {filter.render(filterDropdownProps)}
      </div>
    );
  },
);
